import jwtDecode, { JwtPayload } from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import api from '../../api/apiService';
import { User } from '../../models/Auth';

const moduleUri = '/authentication/v1/auth';
export const LOGIN_URI = `${moduleUri}/login`;
export const SHARED_LOGIN_URI = `${moduleUri}/shared_login`;
export const LOGOUT_URI = `${moduleUri}/logout`;
export const REFRESH_TOKEN_URI = `${moduleUri}/token`;
export const LOGIN_BY_SSO_URI = '/authentication/v1/sso/saml';
export const REFRESH_SSO_TOKEN_URI = '/authentication/v1/sso/saml/token';

const ACCESS_TOKEN_NAME = 'csci-access-token';
const REFRESH_TOKEN_NAME = 'csci-refresh-token';
const IS_SSO_TOKEN_NAME = 'csci-is-sso-token';

const APPLICATION_ID = 'fci';

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_NAME);
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_NAME);
const getIsSSOToken = (): boolean => localStorage.getItem(IS_SSO_TOKEN_NAME) === 'true';

export const auth$ = new BehaviorSubject(getAccessToken());
let refreshTokenIntervalId;

export const signIn = async (username: string, password: string): Promise<void> => {
  const { data } = await api.post(LOGIN_URI, {
    username,
    password,
    applicationId: APPLICATION_ID,
  });
  const { accessToken, refreshToken } = data;

  setTokens(accessToken, refreshToken, false);
  auth$.next(accessToken);
};

export const redirectToSSO = (ssoId: string, redirect = ''): void => {
  window.location.href = `/api${LOGIN_BY_SSO_URI}?ssoid=${encodeURIComponent(ssoId)}&redirect=${encodeURIComponent(
    redirect
  )}`;
};

export const signInBySSO = async (): Promise<void> => {
  await refreshAccessToken(true);
  auth$.next(getAccessToken());
};

export const refreshAccessToken = async (bySSO?: boolean): Promise<JwtPayload | null> => {
  try {
    const isSSO = bySSO ?? getIsSSOToken();
    const { data } = await api.get(
      isSSO ? REFRESH_SSO_TOKEN_URI : REFRESH_TOKEN_URI,
      isSSO
        ? undefined
        : {
            headers: {
              authorization: `Bearer ${getRefreshToken()}`,
            },
          }
    );
    const { accessToken, refreshToken } = data;

    setTokens(accessToken, refreshToken, isSSO);
    return decode(accessToken);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const signInBySharedLogin = async (token: string): Promise<void> => {
  const { data } = await api.post(SHARED_LOGIN_URI, {
    token,
    applicationId: APPLICATION_ID,
  });

  const { accessToken, refreshToken } = data;
  setTokens(accessToken, refreshToken, false);
  auth$.next(accessToken);
};

export const onTokenChange = async (token: string | null): Promise<void> => {
  if (token) {
    const user = await refreshAccessToken();
    if (user) {
      refreshTokenIntervalId = setInterval(refreshAccessToken, 298 * 1000);
    }
  } else {
    clearInterval(refreshTokenIntervalId);
  }
};

export const signOut = async (): Promise<void> => {
  try {
    const { data } = await api.get(LOGOUT_URI);
    const { fitchAuthLogoutUrl } = data;
    if (fitchAuthLogoutUrl) {
      setTimeout(() => {
        window.location.replace(fitchAuthLogoutUrl);
      }, 100);
    }
  } catch (e) {}
  clearTokens();
  auth$.next(null);
};

const setTokens = (accessToken, refreshToken, isSSO) => {
  localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
  localStorage.setItem(IS_SSO_TOKEN_NAME, isSSO ? 'true' : 'false');
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
  }
};

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_NAME);
  localStorage.removeItem(REFRESH_TOKEN_NAME);
};

export const decode = (token: string): JwtPayload => jwtDecode(token);

export const getCurrentUser = (): User => {
  const token = auth$.value;

  if (token) {
    const user: JwtPayload = decode(token);
    return { userId: user.sub, exp: user.exp };
  }

  return {};
};
